import React, { useState, useRef } from 'react';

import MultiSelectField from './Product3Comp/MultiSelectField';
import CombinationsTable from './Product3Comp/CombinationsTable';
import type { Group, ProductField, SelectedOptions } from '@univdiam/ui/type';
import { useCombinations } from '@univdiam/ui/hooks/useCombinations';
import { Card, CardContent, CardHeader, CardTitle } from '@univdiam/ui/components/card';
import RichTextEditor from '../RichTextEditor';
import { Button } from '@univdiam/ui/components/button';
import MediaUploader from '../MediaUploader';
import { Label } from '@univdiam/ui/components/label';
import { Input } from '@univdiam/ui/components/input';
import { Trash, Loader2, GripVertical } from 'lucide-react';
import { toast } from 'sonner';


type SendDataType = {
  message: string;
  media_url?: string
  content_type: string
  metdata?: string
}

/** Existing product data used to prefill the form when editing. */
type ProductInitialData = {
  /** Raw saved combinations: [{ id, combination: {...}, price }]. */
  bom?: any[];
  attachments?: string[];
  iframe?: string;
  instruction?: string;
  customFields?: { id: string; label: string; value: string }[];
};

/** Reverse-derive which option ids are selected per field from saved combinations. */
const deriveSelectedOptions = (bom: any[] | undefined, fields: ProductField[]): SelectedOptions => {
  const result: SelectedOptions = {};
  if (!Array.isArray(bom)) return result;
  for (const item of bom) {
    const comb = item?.combination ?? item;
    if (!comb) continue;
    for (const field of fields) {
      const key = field.label.toLowerCase().replace(/\s+/g, '');
      const val = comb[key];
      if (val == null) continue;
      const opt = field.options.find(o => o.value === String(val));
      if (!opt) continue;
      const arr = result[field.id] ?? (result[field.id] = []);
      if (!arr.includes(opt.id)) arr.push(opt.id);
    }
  }
  return result;
};

/** Rehydrate per-combination prices keyed by the deterministic combination id. */
const derivePrices = (bom: any[] | undefined): Record<string, number> => {
  const result: Record<string, number> = {};
  if (!Array.isArray(bom)) return result;
  for (const item of bom) {
    if (item?.id != null) result[item.id] = Number(item.price) || 0;
  }
  return result;
};


type AddProductProps = {
  handleSendToUpload: (files: File[]) => Promise<string[]>;
  mediaLoading: boolean;
  isDicussionPending: boolean,
  isDiscussionLoading: boolean,
  handleSendDiscussion: (values: SendDataType) => void,
  groupData: Group[]
  /** When provided, the form opens prefilled for editing an existing product. */
  initialData?: ProductInitialData
  /** Submit button label (defaults to "Add product"). */
  submitLabel?: string
};


function AddProduct3({
  handleSendToUpload,
  mediaLoading,
  handleSendDiscussion,
  isDicussionPending,
  groupData,
  initialData,
  submitLabel = 'Add product'
}: AddProductProps) {
  // options is JSON text from the DB; one bad row must not crash the CAD form.
  const [fields] = useState<ProductField[]>(() => groupData.map(g => {
    try {
      return { ...g, options: JSON.parse(g.options) }
    } catch {
      console.error('Bad options JSON for product group', g)
      return { ...g, options: [] }
    }
  }));
  const [attachments, setAttachments] = useState<string[]>(() => initialData?.attachments ?? []);
  const [inst, setInst] = useState(() => initialData?.instruction ?? '');
  const [iframe, setIframe] = useState(() => initialData?.iframe ?? '');
  const [customFields, setCustomFields] = useState<{ id: string; label: string; value: string }[]>(() => initialData?.customFields ?? []);
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldValue, setNewFieldValue] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<SelectedOptions>(() => deriveSelectedOptions(initialData?.bom, fields));
  const [prices, setPrices] = useState<Record<string, number>>(() => derivePrices(initialData?.bom));

  const combinations = useCombinations(fields, selectedOptions, prices);

  const hasDetails = combinations.length > 0 || attachments.length > 0 || inst.trim() !== '' || iframe.trim() !== '' || customFields.length > 0;

  const handleSelectionChange = (fieldId: number, options: string[]) => {
    setSelectedOptions(prev => ({
      ...prev,
      [fieldId]: options
    }));
  };

  const handlePriceChange = (combinationId: string, price: number) => {
    setPrices(prev => ({
      ...prev,
      [combinationId]: price
    }));
  };

  const updateCustomField = (id: string, key: 'label' | 'value', val: string) => {
    setCustomFields((prev) => prev.map((f) => (f.id === id ? { ...f, [key]: val } : f)));
  };

  const dragIndex = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const reorderCustomField = (from: number, to: number) => {
    if (from === to) return;
    setCustomFields((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const getTotalSelectedCount = () => {
    return Object.values(selectedOptions).reduce((total, options) => total + options.length, 0);
  };
  console.log('combinations', combinations)

  return (
    <div className="flex flex-col bg-white text-caption1 w-full relative">
      <div className="rounded-xl border bg-background p-6 mb-8">
        <div className="flex items-center mb-6">
          {getTotalSelectedCount() > 0 && (
            <span className="ml-auto bg-black/10 text-black text-sm font-medium px-3 py-1 rounded-full">
              {getTotalSelectedCount()} options selected
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {fields.map((field) => (
            <MultiSelectField
              key={field.id}
              field={field}
              selectedOptions={selectedOptions[field.id] || []}
              onSelectionChange={handleSelectionChange}
            />
          ))}
        </div>
      </div>

      {/* Combinations Table */}
      <CombinationsTable
        combinations={combinations}
        fields={fields}
        selectedOptions={selectedOptions}
        onPriceChange={handlePriceChange}
      />

      <Card className="shadow-xs mt-[16px] border">
        <CardHeader>
          <CardTitle>Attachments</CardTitle>
        </CardHeader>
        <CardContent>
          <MediaUploader
            handleSendToUpload={async (files) => {
              const urls = await handleSendToUpload(files);
              setAttachments((prev) => [...prev, ...urls]);
            }}
            onRemove={(index) => setAttachments((prev) => prev.filter((_, i) => i !== index))}
            value={attachments}
            mediaLoading={mediaLoading}
          />
          <div className="mt-4 space-y-2">
            <Label>Iframe link</Label>
            <Input placeholder="Paste iframe link" value={iframe} onChange={(e) => setIframe(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-xs mt-[16px] border">
        <CardHeader>
          <CardTitle>Additional Fields</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 items-end">
            <div className="flex-1 space-y-2">
              <Label>Label</Label>
              <Input
                placeholder="e.g. Metal Type"
                value={newFieldLabel}
                onChange={(e) => setNewFieldLabel(e.target.value)}
                maxLength={100}
              />
            </div>
            <div className="flex-1 space-y-2">
              <Label>Value</Label>
              <Input
                placeholder="e.g. 14kt White Gold"
                value={newFieldValue}
                onChange={(e) => setNewFieldValue(e.target.value)}
                maxLength={255}
              />
            </div>
            <Button
              type="button"
              onClick={() => {
                if (!newFieldLabel.trim() || !newFieldValue.trim()) return;
                setCustomFields((prev) => [
                  ...prev,
                  { id: crypto.randomUUID(), label: newFieldLabel.trim(), value: newFieldValue.trim() },
                ]);
                setNewFieldLabel('');
                setNewFieldValue('');
              }}
            >
              Add
            </Button>
          </div>

          {customFields.length > 0 && (
            <div className="mt-4 space-y-2">
              {customFields.map((field, index) => (
                <div
                  key={field.id}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (dragOverIndex !== index) setDragOverIndex(index);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragIndex.current !== null) reorderCustomField(dragIndex.current, index);
                    dragIndex.current = null;
                    setDragOverIndex(null);
                  }}
                  className={`flex gap-2 items-center rounded-md transition-colors ${dragOverIndex === index ? 'bg-black/5' : ''}`}
                >
                  <button
                    type="button"
                    draggable
                    onDragStart={() => { dragIndex.current = index; }}
                    onDragEnd={() => { dragIndex.current = null; setDragOverIndex(null); }}
                    className="cursor-grab active:cursor-grabbing px-1 text-muted-foreground hover:text-foreground"
                  >
                    <GripVertical size={16} />
                  </button>
                  <Input
                    className="flex-1"
                    value={field.label}
                    onChange={(e) => updateCustomField(field.id, 'label', e.target.value)}
                    maxLength={100}
                  />
                  <Input
                    className="flex-1"
                    value={field.value}
                    onChange={(e) => updateCustomField(field.id, 'value', e.target.value)}
                    maxLength={255}
                  />
                  <button
                    type="button"
                    onClick={() => setCustomFields((prev) => prev.filter((f) => f.id !== field.id))}
                  >
                    <Trash size={14} className="cursor-pointer text-muted-foreground hover:text-destructive" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="shadow-xs mt-[16px] border">
        <CardHeader>
          <CardTitle>Additional Instructions</CardTitle>
        </CardHeader>
        <CardContent>
          <RichTextEditor value={inst} onChange={setInst} />
        </CardContent>
      </Card>

      <div className="sticky bottom-0 bg-white border-t p-4 mt-6 -mx-4 -mb-4 flex justify-end gap-3 z-10 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] rounded-b-lg">
        <Button
          disabled={isDicussionPending || !hasDetails}
          className="bg-black text-white px-8 py-2 rounded-md hover:bg-black/90 active:scale-95 transition-all text-sm font-medium ml-auto flex items-center justify-center"
          onClick={() => {
            // Validation: at least one combination must exist
            if (!combinations || combinations.length === 0) {
              toast.error("Please select product options above to generate at least one combination.");
              return;
            }

            // Validation: for each combination, id, metal, diamond, and price are required
            for (let i = 0; i < combinations.length; i++) {
              const comb = combinations[i];
              const combIdx = i + 1;

              if (!comb.id || !comb.id.trim()) {
                toast.error(`Combination #${combIdx} is missing a valid ID.`);
                return;
              }

              // Retrieve metal and diamond values from combination.combination
              const keys = Object.keys(comb.combination || {});
              const metalKey = keys.find(k => k.includes('metal'));

              const metal = metalKey ? comb.combination[metalKey] : null;

              if (!metal || !metal.trim()) {
                toast.error(`Combination #${combIdx} (${comb.id}) is missing a Metal selection.`);
                return;
              }

              if (!comb.price || Number(comb.price) <= 0) {
                toast.error(`Combination #${combIdx} (${comb.id}) must have a price greater than 0.`);
                return;
              }
            }

            const data = {
              message: '',
              content_type: 'CAD',
              metadata: JSON.stringify({
                bom: combinations,
                attachments: attachments,
                instruction: inst,
                iframe: iframe,
                customFields: customFields
              })
            }
            handleSendDiscussion(data)
          }}
        >
          {isDicussionPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            submitLabel
          )}
        </Button>
      </div>
    </div>
  );
}

export default AddProduct3;